import { ValidationError, FetchFailedError, ErrorCode } from './apiError';
import { isLeft, Either } from 'fp-ts/lib/Either';
import { RawApi } from '.';
import {
  footballLogsApiResponse,
  FootballTable,
  FootballLineUp,
  footballLineUpApiResponse,
  FootballCommentary,
  footballCommentaryApiResponse,
  footballStatsApiResponse,
  FootballStats,
} from './validators/football_logs';
import { footballTopScorersApiResponse, FootballTopScorers } from './validators/football_top_scorers';
import { Errors } from 'io-ts';

export interface FootballAPI {
  fetchFootballLogs: (tournamentId: string) => Promise<FootballTable>;
  fetchFootballTopScorers: (tournamentId: string) => Promise<FootballTopScorers>;
  fetchFootballLineUp: (leaguePath: string, eventId: string) => Promise<FootballLineUp | null>;
  fetchFootballStats: (leaguePath: string, eventId: string) => Promise<FootballStats | null>;
  fetchFootballCommentary: (leaguePath: string, eventId: string) => Promise<FootballCommentary | null>;
}

type FootballTab = 'lineups' | 'commentaries' | 'events-stats';
type FootballDecoders = typeof footballLineUpApiResponse | typeof footballStatsApiResponse | typeof footballCommentaryApiResponse;

export class FootballApi extends RawApi implements FootballAPI {
  public async fetchFootballLogs(
    tournamentId: string,
  ): Promise<FootballTable> {
    const response = await this.fetchScoresApi(`/football/v5/tours/${tournamentId}/table-logs`);
    if (!response.ok) {
      throw new FetchFailedError(response.statusText, response.status as ErrorCode, response);
    }
    const jsonResponse: unknown = await response.json();
    const tableLogs = footballLogsApiResponse.decode(jsonResponse);
    if (isLeft(tableLogs)) {
      throw new ValidationError('API Response validation failed', 400, tableLogs);
    }
    return tableLogs.right;
  }

  public async fetchFootballTopScorers(
    tournamentId: string,
  ): Promise<FootballTopScorers> {
    const response = await this.fetchScoresApi(`/football/v5/tours/${tournamentId}/top-scorers`);
    if (!response.ok) {
      throw new FetchFailedError(response.statusText, response.status as ErrorCode, response);
    }
    const jsonResponse: unknown = await response.json();
    const topScorers = footballTopScorersApiResponse.decode(jsonResponse);
    if (isLeft(topScorers)) {
      throw new ValidationError('API Response validation failed', 400, topScorers);
    }
    return topScorers.right;
  }

  public async fetchFootballLineUp(leaguePath: string, eventId: string): Promise<FootballLineUp | null> {
    return this.fetchFootballMatchTab(leaguePath, eventId, 'lineups', footballLineUpApiResponse);
  }

  public async fetchFootballCommentary(leaguePath: string, eventId: string): Promise<FootballCommentary | null> {
    return this.fetchFootballMatchTab(leaguePath, eventId, 'commentaries', footballCommentaryApiResponse);
  }

  public async fetchFootballStats(leaguePath: string, eventId: string): Promise<FootballStats | null> {
    return this.fetchFootballMatchTab(leaguePath, eventId, 'events-stats', footballStatsApiResponse);
  }

  private async fetchFootballMatchTab<T extends FootballDecoders = FootballDecoders, U = ReturnType<T['decode']>>(
    leaguePath: string,
    eventId: string,
    matchTab: FootballTab,
    decoder: T,
  ): Promise<U | null> {
    const response = await this.fetchScoresApi(`/football/v5/${leaguePath}/matches/${eventId}/${matchTab}`);

    if (!response.ok) {
      throw new FetchFailedError(response.statusText, response.status as ErrorCode, response);
    }

    if (response.status === 204) {
      return null;
    }

    const jsonResponse: unknown = await response.json();

    const footballTab = decoder.decode(jsonResponse) as Either<Errors, U>;
    if (isLeft(footballTab)) {
      throw new ValidationError('API Response validation failed', 400, footballTab);
    }

    return footballTab.right;
  }
}
